import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { Trans, useTranslation } from 'react-i18next';
import { closeDialog } from '@shared/store/dialog.duck';

const STAR_ICON = '/assets/icons/standard/Star.svg';

interface Props {
  // The plan selected in the UpgradeSection: its label translation key and star count. bodyKey
  // optionally overrides the default body copy (e.g. the Enterprise account-manager message).
  payload?: { label: string; stars: number; bodyKey?: string };
}

export function UpgradeContactDialog({ payload }: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const close = () => dispatch(closeDialog());

  return (
    <StyledDialog isOpen={true} onClose={close} showLogo={true}>
      <ContentContainer>
        <DialogHeader title={'payments.upgrade-contact-title'} />

        {payload &&
          <PlanSummary>
            <PlanPill>
              <BodyRegular weight={600} colour={Colours.blue}>{t(payload.label)}</BodyRegular>
            </PlanPill>
            <StarRow>
              {Array.from({ length: payload.stars }).map((_, index) => (
                <StarIcon key={index} src={STAR_ICON} alt="" aria-hidden={true} />
              ))}
            </StarRow>
          </PlanSummary>}

        <ManageSection>
          <BodyLarge weight={600}>{t('payments.manage-plan-title')}</BodyLarge>
          <BodyRegular style={{ marginTop: 8 }}>
            <Trans i18nKey={payload?.bodyKey ?? 'payments.manage-plan-body'}
                   components={[<a style={{ color: Colours.blue, fontWeight: 600 }} href={'mailto:hello@team-today.com'}>hello@team-today.com</a>]} />
          </BodyRegular>
        </ManageSection>
      </ContentContainer>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)<any>`
  .dialog__content {
    max-width: 680px;
    padding: 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 100%;
  width: 600px;
`;

const ManageSection = styled.div`
  margin-top: 24px;
`;

const PlanSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const PlanPill = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${Colours.blue};
  border-radius: 64px;
  padding: 4px 18px;
`;

const StarRow = styled.div`
  display: flex;
  gap: 6px;
`;

const StarIcon = styled.img`
  display: block;
  width: 48px;
  height: 48px;
`;
